import ConflictingTripsModal from '@/components/manage/change-trips/ConflictingTripsModal';
import TripOverview from '@/components/manage/change-trips/TripOverview';
import TripPassengerListPopover from '@/components/manage/change-trips/TripPassengerListPopover';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import {
  changeTripsOutboundTripIdSelector,
  changeTripsReturnTripIdSelector,
  manageFlowBookingSelector,
} from '@/features/manage/manageSelectors';
import { useBookingTrips } from '@/hooks/useBookingTrips';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import {
  isTripChangeDisabled,
  isTripWhollyInactive,
} from '@/utils/changeTrips';
import { cn } from '@/utils/cn';
import { Button, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { Fragment, useMemo, useState, type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

// Cancel/Confirm footer shared by ChangeTripsModal and (in spirit, by hand)
// by ConflictingTripsModal. Kept inside this file because it's used by a
// single component and we don't want to spread the change-trips surface
// across more files than necessary.
interface ChangeTripsFooterProps {
  isLaptopOrBigger: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isConfirmDisabled: boolean;
  cancelTestId: string;
  confirmTestId: string;
}

const ChangeTripsFooter: FC<ChangeTripsFooterProps> = ({
  isLaptopOrBigger,
  onCancel,
  onConfirm,
  isConfirmDisabled,
  cancelTestId,
  confirmTestId,
}) => {
  const buttonSize = isLaptopOrBigger ? 'regular' : 'large';
  return (
    <div className="flex w-full gap-2 laptop:flex-row laptop:justify-end">
      <Button
        variant="tertiary"
        type="button"
        onClick={onCancel}
        data-testid={cancelTestId}
        size={buttonSize}
        className="rounded-lg laptop:w-auto"
      >
        <TransText i18nKey="cancel" />
      </Button>
      <Button
        type="button"
        onClick={onConfirm}
        isDisabled={isConfirmDisabled}
        data-testid={confirmTestId}
        size={buttonSize}
        className="rounded-lg laptop:w-auto"
        startAdornment={<Icons.check />}
      >
        <TransText i18nKey="confirm" />
      </Button>
    </div>
  );
};

interface ChangeTripsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TripSection {
  trips: Array<BookedTripDTO>;
  title: ReactNode;
}

// Renders one trip row inside a section. When the section contains more than
// one trip we additionally render the "Trip N" header + passenger popover
// above the card. Dimmed styling kicks in for mixed-offer / non-exchangeable
// trips, where the card itself is also disabled.
const TripRow: FC<{
  trip: BookedTripDTO;
  tripIndex: number;
  showHeader: boolean;
}> = ({ trip, tripIndex, showHeader }) => {
  const isDimmed = isTripChangeDisabled(trip);
  return (
    <div className="flex flex-col gap-1.5">
      {showHeader && (
        <div className="flex w-full flex-row items-center justify-between">
          <Typography
            variant="body2-bold"
            className={cn(isDimmed && 'text-neutral')}
          >
            <TransText
              i18nKey="tripNumber"
              values={{ number: tripIndex + 1 }}
            />
          </Typography>
          <TripPassengerListPopover trip={trip} isDimmed={isDimmed} />
        </div>
      )}
      <TripOverview trip={trip} />
    </div>
  );
};

const TripSectionBlock: FC<{ section: TripSection }> = ({ section }) => (
  <div>
    <Typography variant="body1-bold" className="mb-3">
      {section.title}
    </Typography>
    <div className="flex flex-col gap-5">
      {section.trips.map((trip, tripIndex) => (
        <TripRow
          key={trip.id}
          trip={trip}
          tripIndex={tripIndex}
          showHeader={section.trips.length > 1}
        />
      ))}
    </div>
  </div>
);

const ChangeTripsModal: FC<ChangeTripsModalProps> = ({ isOpen, onClose }) => {
  const booking = useSelector(manageFlowBookingSelector);
  const { outboundTrips, inboundTrips } = useBookingTrips(booking);
  const activeOutboundTrips = useMemo(
    () => outboundTrips.filter((trip) => !isTripWhollyInactive(trip)),
    [outboundTrips]
  );
  const activeInboundTrips = useMemo(
    () => inboundTrips.filter((trip) => !isTripWhollyInactive(trip)),
    [inboundTrips]
  );
  const outboundTripId = useSelector(changeTripsOutboundTripIdSelector);
  const returnTripId = useSelector(changeTripsReturnTripIdSelector);
  const isConfirmEnabled = Boolean(outboundTripId || returnTripId);
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  const { conflictReason } = useChangeBookingSelection();
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleConfirm = () => {
    if (conflictReason !== null) {
      setIsConflictModalOpen(true);
      return;
    }
    navigate('/purchase');
  };

  const title: ReactNode = (
    <div className="flex flex-col gap-1 laptop:flex-row laptop:items-baseline laptop:gap-2">
      <Typography variant="subtitle">
        <TransText i18nKey="changeTrips" />
      </Typography>
      {booking?.code && (
        <Typography variant="body1" className="text-neutral">
          <TransText i18nKey="booking" /> {booking.code}
        </Typography>
      )}
    </div>
  );

  const sections: Array<TripSection> = [
    {
      trips: activeOutboundTrips,
      title: (
        <TransText
          i18nKey="outboundTrips"
          values={{ count: activeOutboundTrips.length }}
        />
      ),
    },
    {
      trips: activeInboundTrips,
      title: (
        <TransText
          i18nKey="returnTrips"
          values={{ count: activeInboundTrips.length }}
        />
      ),
    },
  ];

  const showDividerBetweenSections =
    !!activeOutboundTrips.length && !!activeInboundTrips.length;

  return (
    <>
      <ConflictingTripsModal
        isOpen={isConflictModalOpen}
        onClose={() => setIsConflictModalOpen(false)}
      />
      <ResponsiveDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={title}
        footer={
          <ChangeTripsFooter
            isLaptopOrBigger={isLaptopOrBigger}
            onCancel={onClose}
            onConfirm={handleConfirm}
            isConfirmDisabled={!isConfirmEnabled}
            cancelTestId="change-trips-cancel"
            confirmTestId="change-trips-confirm"
          />
        }
        content={
          <div className="p-4" data-testid="change-trips-modal-content">
            {sections.map((section, index) => {
              if (section.trips.length === 0) return null;
              return (
                <Fragment key={index}>
                  {index > 0 && showDividerBetweenSections && (
                    <Divider className="my-4" variant="medium" />
                  )}
                  <TripSectionBlock section={section} />
                </Fragment>
              );
            })}
          </div>
        }
      />
    </>
  );
};

export default ChangeTripsModal;
